import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  useAdminSyncStripeCustomerById,
  useAdminSyncStripeCustomerByUserId,
  useAdminSyncStripeCustomerByOrganizationId,
} from '@/api/auth/admin/admin';

type SyncType = 'stripeCustomerId' | 'userId' | 'organizationId';

interface SyncCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSyncStarted: () => void;
}

/**
 * Dialog for syncing a specific Stripe customer by various identifiers.
 */
export function SyncCustomerDialog({
  open,
  onOpenChange,
  onSyncStarted,
}: SyncCustomerDialogProps) {
  const [syncType, setSyncType] = useState<SyncType>('stripeCustomerId');
  const [identifier, setIdentifier] = useState('');

  const syncByCustomerId = useAdminSyncStripeCustomerById({
    mutation: {
      onSuccess: (data) => {
        toast.success('Sync started', {
          description: `Orchestration ${data.data.instanceId} is running.`,
        });
        handleClose();
        onSyncStarted();
      },
      onError: (error) => {
        toast.error('Sync failed', {
          description: (error as Error)?.message || 'Unknown error',
        });
      },
    },
  });

  const syncByUserId = useAdminSyncStripeCustomerByUserId({
    mutation: {
      onSuccess: (data) => {
        toast.success('Sync started', {
          description: `Orchestration ${data.data.instanceId} is running.`,
        });
        handleClose();
        onSyncStarted();
      },
      onError: (error) => {
        toast.error('Sync failed', {
          description: (error as Error)?.message || 'Unknown error',
        });
      },
    },
  });

  const syncByOrganizationId = useAdminSyncStripeCustomerByOrganizationId({
    mutation: {
      onSuccess: (data) => {
        toast.success('Sync started', {
          description: `Orchestration ${data.data.instanceId} is running.`,
        });
        handleClose();
        onSyncStarted();
      },
      onError: (error) => {
        toast.error('Sync failed', {
          description: (error as Error)?.message || 'Unknown error',
        });
      },
    },
  });

  const isPending =
    syncByCustomerId.isPending ||
    syncByUserId.isPending ||
    syncByOrganizationId.isPending;

  const handleClose = () => {
    setIdentifier('');
    setSyncType('stripeCustomerId');
    onOpenChange(false);
  };

  const handleSync = () => {
    if (!identifier.trim()) {
      toast.error('Please enter an identifier');
      return;
    }

    switch (syncType) {
      case 'stripeCustomerId':
        syncByCustomerId.mutate({ customerId: identifier.trim() });
        break;
      case 'userId':
        syncByUserId.mutate({ userId: identifier.trim() });
        break;
      case 'organizationId':
        syncByOrganizationId.mutate({ organizationId: identifier.trim() });
        break;
    }
  };

  const getPlaceholder = () => {
    switch (syncType) {
      case 'stripeCustomerId':
        return 'cus_xxxxxxxxxxxxx';
      case 'userId':
        return '00000000-0000-0000-0000-000000000000';
      case 'organizationId':
        return '00000000-0000-0000-0000-000000000000';
    }
  };

  const getLabel = () => {
    switch (syncType) {
      case 'stripeCustomerId':
        return 'Stripe Customer ID';
      case 'userId':
        return 'User ID';
      case 'organizationId':
        return 'Organization ID';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Sync Stripe Customer</DialogTitle>
          <DialogDescription>
            Sync a specific customer's data from Stripe by providing an identifier.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="syncType">Identifier Type</Label>
            <Select
              value={syncType}
              onValueChange={(value) => setSyncType(value as SyncType)}
            >
              <SelectTrigger id="syncType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripeCustomerId">Stripe Customer ID</SelectItem>
                <SelectItem value="userId">User ID</SelectItem>
                <SelectItem value="organizationId">Organization ID</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="identifier">{getLabel()}</Label>
            <Input
              id="identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={getPlaceholder()}
              className="font-mono"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSync} disabled={isPending || !identifier.trim()}>
            {isPending ? 'Syncing...' : 'Sync Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
